// src/utils/dateTimeSystem.ts

/**
 * Sistema de conversión de fechas entre el backend y el frontend
 * Maneja las diferencias de timezone y formato entre la API y la UI
 */
export class DateTimeSystem {
    /**
     * Lista de campos que típicamente contienen fechas
     */
    private static readonly DATE_FIELDS: string[] = [
        'createdAt',
        'updatedAt',
        'deletedAt',
        'lastLoginAt',
        'expiresAt',
        'startsAt',
        'endsAt',
        'assignedAt',
        'completedAt',
        'lastMaintenanceAt',
        'checkInDate',
        'checkOutDate',
        'reservationDate',
        'birthDate',
        'hireDate',
        'terminationDate',
        'lastModified',
        'dateCreated',
        'dateUpdated',
        'timestamp',
        'date',
        'datetime',
        'created',
        'updated',
        'modified'
    ];

    /**
     * Convierte las fechas del formato del backend al formato del frontend
     * @param data - Datos del backend
     * @returns Datos con fechas convertidas
     */
    static convertFromBackend<T = any>(data: T): T {
        if (!data) return data;
        
        // Si es un array, procesar cada elemento
        if (Array.isArray(data)) {
            return data.map(item => this.convertFromBackend(item)) as T;
        }
        
        // Si es un objeto, procesar cada propiedad
        if (typeof data === 'object' && data !== null) {
            const converted: any = {};
            
            for (const [key, value] of Object.entries(data)) {
                // Si el campo es una fecha conocida y tiene valor
                if (this.isDateField(key) && value) {
                    converted[key] = this.parseDate(value);
                }
                // Si el valor es un objeto o array, procesar recursivamente
                else if (typeof value === 'object' && value !== null) {
                    converted[key] = this.convertFromBackend(value);
                }
                // Si es una cadena que parece una fecha ISO
                else if (typeof value === 'string' && this.isISODateString(value)) {
                    converted[key] = this.parseDate(value);
                }
                // Mantener el valor tal cual
                else {
                    converted[key] = value;
                }
            }
            
            return converted as T;
        }
        
        // Si es una cadena que parece una fecha, convertirla
        if (typeof data === 'string' && this.isISODateString(data)) {
            return this.parseDate(data) as T;
        }
        
        return data;
    }
    
    /**
     * Convierte las fechas del formato del frontend al formato del backend
     * @param data - Datos del frontend
     * @returns Datos con fechas convertidas para el backend
     */
    static convertForBackend<T = any>(data: T): T {
        if (!data) return data;
        
        // Si es un array, procesar cada elemento
        if (Array.isArray(data)) {
            return data.map(item => this.convertForBackend(item)) as T;
        }
        
        // Si es un objeto Date, convertir a ISO string
        if (data instanceof Date) {
            return data.toISOString() as T;
        }
        
        // Si es un objeto, procesar cada propiedad
        if (typeof data === 'object' && data !== null) {
            const converted: any = {};
            
            for (const [key, value] of Object.entries(data)) {
                // Si el valor es una fecha
                if (value instanceof Date) {
                    converted[key] = value.toISOString();
                }
                // Si el valor es un objeto o array, procesar recursivamente
                else if (typeof value === 'object' && value !== null) {
                    converted[key] = this.convertForBackend(value);
                }
                // Mantener el valor tal cual
                else {
                    converted[key] = value;
                }
            }
            
            return converted as T;
        }
        
        return data;
    }
    
    /**
     * Verifica si un campo es un campo de fecha
     * @param fieldName - Nombre del campo
     * @returns True si es un campo de fecha
     */
    private static isDateField(fieldName: string): boolean {
        const lowerFieldName = fieldName.toLowerCase();
        return this.DATE_FIELDS.some(field => 
            lowerFieldName.includes(field.toLowerCase())
        );
    }
    
    /**
     * Verifica si una cadena parece ser una fecha ISO
     * @param str - Cadena a verificar
     * @returns True si parece una fecha ISO
     */
    static isISODateString(str: string): boolean {
        if (typeof str !== 'string') return false;
        
        // Patrones comunes de fechas ISO
        // 2024-01-15T10:30:00Z
        // 2024-01-15T10:30:00.000Z
        // 2024-01-15T10:30:00+00:00
        // 2024-01-15T10:30:00-05:00
        const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,7})?(Z|[+-]\d{2}:\d{2})?$/;
        
        // También verificar fechas simples (sin hora)
        // 2024-01-15
        const simpleDatePattern = /^\d{4}-\d{2}-\d{2}$/;
        
        // Fecha y hora sin T (algunos backends lo envían así)
        // 2024-01-15 10:30:00
        const dateTimePattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d{1,7})?$/;
        
        return isoDatePattern.test(str) || simpleDatePattern.test(str) || dateTimePattern.test(str);
    }
    
    /**
     * Parsea una fecha del backend
     * @param dateString - Cadena de fecha
     * @returns Fecha parseada o null
     */
    static parseDate(dateString: string | Date | null | undefined): string | null {
        if (!dateString) return null;
        
        // Si ya es un objeto Date válido
        if (dateString instanceof Date && !isNaN(dateString.getTime())) {
            return dateString.toISOString();
        }
        
        // Si es una cadena
        if (typeof dateString === 'string') {
            try {
                // Manejar formato "2024-01-15 10:30:00" convirtiéndolo a ISO
                const normalizedDate = dateString.replace(' ', 'T');
                const date = new Date(normalizedDate);
                
                // Verificar que la fecha es válida
                if (isNaN(date.getTime())) {
                    console.warn('Invalid date:', dateString);
                    return dateString; // Devolver la cadena original si no es válida
                }
                
                // Para React Admin, es mejor devolver la cadena ISO
                return date.toISOString();
            } catch (error) {
                console.warn('Error parsing date:', dateString, error);
                return dateString; // Devolver la cadena original en caso de error
            }
        }
        
        return null;
    }
    
    /**
     * Formatea una fecha para mostrar en la UI
     * @param date - Fecha a formatear
     * @param format - Formato deseado
     * @returns Fecha formateada
     */
    static formatForDisplay(
        date: string | Date | null | undefined, 
        format: 'date' | 'time' | 'datetime' = 'datetime'
    ): string {
        if (!date) return '';
        
        try {
            const dateObj = date instanceof Date ? date : new Date(date);
            
            if (isNaN(dateObj.getTime())) {
                return '';
            }
            
            const options: Record<string, Intl.DateTimeFormatOptions> = {
                date: {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                },
                time: {
                    hour: '2-digit',
                    minute: '2-digit'
                },
                datetime: {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                }
            };
            
            // Usar el locale del navegador o español por defecto
            const locale = navigator.language || 'es-ES';
            
            return dateObj.toLocaleString(locale, options[format]);
        } catch (error) {
            console.warn('Error formatting date:', date, error);
            return '';
        }
    }
    
    /**
     * Obtiene la diferencia en días entre dos fechas
     * @param date1 - Primera fecha
     * @param date2 - Segunda fecha
     * @returns Diferencia en días
     */
    static daysDifference(
        date1: string | Date | null | undefined, 
        date2: string | Date | null | undefined
    ): number {
        if (!date1 || !date2) return 0;
        
        try {
            const d1 = date1 instanceof Date ? date1 : new Date(date1);
            const d2 = date2 instanceof Date ? date2 : new Date(date2);
            
            if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
                return 0;
            }
            
            const diffTime = Math.abs(d2.getTime() - d1.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            return diffDays;
        } catch (error) {
            console.warn('Error calculating days difference:', error);
            return 0;
        }
    }
    
    /**
     * Verifica si una fecha está en el pasado
     * @param date - Fecha a verificar
     * @returns True si está en el pasado
     */
    static isPast(date: string | Date | null | undefined): boolean {
        if (!date) return false;
        
        try {
            const dateObj = date instanceof Date ? date : new Date(date);
            return dateObj < new Date();
        } catch {
            return false;
        }
    }
    
    /**
     * Verifica si una fecha está en el futuro
     * @param date - Fecha a verificar
     * @returns True si está en el futuro
     */
    static isFuture(date: string | Date | null | undefined): boolean {
        if (!date) return false;
        
        try {
            const dateObj = date instanceof Date ? date : new Date(date);
            return dateObj > new Date();
        } catch {
            return false;
        }
    }
    
    /**
     * Obtiene el inicio del día para una fecha
     * @param date - Fecha
     * @returns Fecha al inicio del día
     */
    static startOfDay(date: string | Date = new Date()): Date {
        const dateObj = date instanceof Date ? new Date(date) : new Date(date);
        dateObj.setHours(0, 0, 0, 0);
        return dateObj;
    }
    
    /**
     * Obtiene el final del día para una fecha
     * @param date - Fecha
     * @returns Fecha al final del día
     */
    static endOfDay(date: string | Date = new Date()): Date {
        const dateObj = date instanceof Date ? new Date(date) : new Date(date);
        dateObj.setHours(23, 59, 59, 999);
        return dateObj;
    }
    
    /**
     * Añade días a una fecha
     * @param date - Fecha base
     * @param days - Número de días a añadir
     * @returns Nueva fecha
     */
    static addDays(date: string | Date, days: number): Date {
        const dateObj = date instanceof Date ? new Date(date) : new Date(date);
        dateObj.setDate(dateObj.getDate() + days);
        return dateObj;
    }
    
    /**
     * Resta días a una fecha
     * @param date - Fecha base
     * @param days - Número de días a restar
     * @returns Nueva fecha
     */
    static subtractDays(date: string | Date, days: number): Date {
        return this.addDays(date, -days);
    }
    
    /**
     * Formats a relative time («hace 5 minutos» / «5 minutes ago») in the UI language.
     * @param date - Date to format
     * @param locale - UI locale (`es`, `en`, `en-GB`…); Spanish when absent or unknown
     * @param now - Reference instant (tests)
     */
    static formatRelative(
        date: string | Date | null | undefined,
        locale: string = 'es',
        now: Date = new Date()
    ): string {
        if (!date) return '';

        try {
            const dateObj = date instanceof Date ? date : new Date(date);
            if (isNaN(dateObj.getTime())) return '';
            const english = locale.toLowerCase().startsWith('en');
            const diffMs = now.getTime() - dateObj.getTime();
            const diffSeconds = Math.floor(diffMs / 1000);
            const diffMinutes = Math.floor(diffSeconds / 60);
            const diffHours = Math.floor(diffMinutes / 60);
            const diffDays = Math.floor(diffHours / 24);

            const unit = (n: number, es: [string, string], en: [string, string]) => english
                ? `${n} ${n === 1 ? en[0] : en[1]} ago`
                : `hace ${n} ${n === 1 ? es[0] : es[1]}`;

            if (diffSeconds < 60) {
                return english ? 'a few seconds ago' : 'hace unos segundos';
            } else if (diffMinutes < 60) {
                return unit(diffMinutes, ['minuto', 'minutos'], ['minute', 'minutes']);
            } else if (diffHours < 24) {
                return unit(diffHours, ['hora', 'horas'], ['hour', 'hours']);
            } else if (diffDays < 30) {
                return unit(diffDays, ['día', 'días'], ['day', 'days']);
            } else {
                return dateObj.toLocaleDateString(english ? 'en-GB' : 'es-ES', {
                    year: 'numeric', month: '2-digit', day: '2-digit'
                });
            }
        } catch (error) {
            console.warn('Error formatting relative date:', date, error);
            return '';
        }
    }
}